import { ChevronDown, ChevronUp } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { useTheme } from '@/app/ThemeContext';
import {
  getAllAliasesForEntity,
  getAllEntities,
  getQueryEntity,
} from '@/sections/insights/queryBuilder/entities';
import type { FlatGraphNode } from '@/sections/insights/queryBuilder/types/FlatGraph';
import { getIconOrDefault } from '@/sections/insights/queryBuilder/utils/graphIcons';

import { NodeDeleteButton } from './NodeDeleteButton';

interface NodeLabelProps {
  node: FlatGraphNode;
  isExpanded: boolean;
  editable: boolean;
  firstLabel: string;
  filterCount: number;
  onToggle: (nodeId: string) => void;
  onUpdateLabel?: (nodeId: string, newLabel: string) => void;
  handleLabelChange: (newLabel: string) => void;
  onDeleteNode?: (nodeId: string) => void;
  isDeletable?: boolean;
}

/**
 * NodeLabel - Displays node icon, label (editable select or display), and filter count
 */
export const NodeLabel = ({
  node,
  isExpanded,
  editable,
  firstLabel,
  filterCount,
  onToggle,
  handleLabelChange,
  onDeleteNode,
  isDeletable = false,
}: NodeLabelProps) => {
  const { currentTheme } = useTheme();
  const { icon } = getIconOrDefault(firstLabel, currentTheme);

  // Resolve the entity from firstLabel (might be alias or base)
  const resolvedEntity = getQueryEntity(firstLabel);

  // Determine if firstLabel is an alias
  const isAlias = resolvedEntity?.alias !== undefined;

  // Get base entity (if alias, resolve to base; otherwise use resolved)
  const baseEntity = isAlias
    ? getQueryEntity(resolvedEntity.alias!)
    : resolvedEntity;

  // Get all aliases for the base entity (for secondary dropdown)
  const aliases = baseEntity ? getAllAliasesForEntity(baseEntity) : [];

  // Determine if secondary dropdown should be shown
  const showSecondarySelect = aliases.length > 0;

  // Track secondary selection state
  const [selectedSecondary, setSelectedSecondary] = useState<string>(
    isAlias ? resolvedEntity.primaryLabel.actual : ''
  );

  // Track previous firstLabel to detect changes
  const prevFirstLabel = useRef(firstLabel);

  // Update selectedSecondary when firstLabel changes (e.g., opening new query)
  useEffect(() => {
    if (prevFirstLabel.current !== firstLabel) {
      const newResolvedEntity = getQueryEntity(firstLabel);
      const newIsAlias = newResolvedEntity?.alias !== undefined;
      const newSecondary = newIsAlias
        ? newResolvedEntity.primaryLabel.actual
        : '';
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedSecondary(newSecondary);
      prevFirstLabel.current = firstLabel;
    }
  }, [firstLabel]);

  return (
    <div
      onClick={() => onToggle(node.id)}
      className="hover:bg-chariot-background-secondary flex w-full cursor-pointer items-center gap-3 rounded-t-lg p-4 text-left"
      data-testid={`node-card-${node.id}`}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle(node.id);
        }
      }}
      aria-expanded={isExpanded}
      aria-controls={`node-content-${node.id}`}
      aria-label={`${firstLabel} node with ${filterCount} filter${filterCount > 1 ? 's' : ''}`}
    >
      <img src={icon} alt={firstLabel} className="h-8 w-8 rounded-full" />
      <div className="flex-1">
        {editable ? (
          <>
            <select
              value={baseEntity?.primaryLabel.actual}
              onChange={e => {
                const newPrimaryLabel = e.target.value;

                // Always update the label immediately (fixes AD bug)
                handleLabelChange(newPrimaryLabel);

                // Reset secondary dropdown (no longer dependent on alias check)
                setSelectedSecondary('');
              }}
              onClick={e => e.stopPropagation()}
              className="text-chariot-text-primary focus:ring-chariot-brand-primary cursor-pointer rounded border-none bg-transparent font-medium hover:underline focus:ring-2 focus:outline-none"
              aria-label="Change entity type"
              data-testid={`label-select-${node.id}`}
            >
              <option value="">Select type...</option>
              {getAllEntities()
                .sort((a, b) =>
                  (
                    a.primaryLabel.display || a.primaryLabel.actual
                  ).localeCompare(
                    b.primaryLabel.display || b.primaryLabel.actual
                  )
                )
                .map(entity => (
                  <option
                    key={entity.primaryLabel.actual}
                    value={entity.primaryLabel.actual}
                  >
                    {entity.primaryLabel.display || entity.primaryLabel.actual}
                  </option>
                ))}
            </select>

            {/* Secondary select - show if base entity has aliases */}
            {showSecondarySelect && (
              <select
                value={selectedSecondary}
                onChange={e => {
                  const newSecondary = e.target.value;
                  setSelectedSecondary(newSecondary);

                  if (newSecondary) {
                    // User selected an alias - use alias label
                    handleLabelChange(newSecondary);
                  } else {
                    // User deselected (placeholder) - use base entity label
                    handleLabelChange(baseEntity!.primaryLabel.actual);
                  }
                }}
                onClick={e => e.stopPropagation()}
                className="text-chariot-text-secondary focus:ring-chariot-brand-primary mt-2 ml-4 cursor-pointer rounded border-none bg-transparent font-medium hover:underline focus:outline-none"
                aria-label="Change entity subtype"
                data-testid={`secondary-label-select-${node.id}`}
              >
                <option value="">Select type...</option>
                {aliases
                  .sort((a, b) =>
                    (
                      a.primaryLabel.display || a.primaryLabel.actual
                    ).localeCompare(
                      b.primaryLabel.display || b.primaryLabel.actual
                    )
                  )
                  .map(alias => (
                    <option
                      key={alias.primaryLabel.actual}
                      value={alias.primaryLabel.actual}
                    >
                      {alias.primaryLabel.display || alias.primaryLabel.actual}
                    </option>
                  ))}
              </select>
            )}
          </>
        ) : (
          <div className="text-chariot-text-primary font-medium">
            {baseEntity?.primaryLabel.display ||
              baseEntity?.primaryLabel.actual ||
              firstLabel}
            {isAlias && (
              <span className="text-chariot-text-secondary ml-2">
                (
                {resolvedEntity.primaryLabel.display ||
                  resolvedEntity.primaryLabel.actual}
                )
              </span>
            )}
          </div>
        )}
      </div>
      {editable && onDeleteNode && (
        <NodeDeleteButton
          onDelete={() => onDeleteNode(node.id)}
          disabled={!isDeletable}
          nodeId={node.id}
        />
      )}
      <div className="flex flex-col items-center gap-1">
        {isExpanded ? (
          <ChevronUp className="text-chariot-text-primary" />
        ) : (
          <ChevronDown className="text-chariot-text-primary" />
        )}
        <div className="text-chariot-text-secondary text-center text-xs">
          {filterCount === 0
            ? 'No filters'
            : `${filterCount} filter${filterCount > 1 ? 's' : ''}`}
        </div>
      </div>
    </div>
  );
};
